import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  RefreshCw, Loader2, Cloud, CheckCircle2, AlertTriangle, Users2, Merge,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function fmtDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleString("fr-CH", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Integrations() {
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="integrations-page">
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2 mb-1">
        <Cloud className="w-7 h-7 text-[#52a675]" /> Intégrations & qualité CRM
      </h1>
      <p className="text-sm text-gray-500 mb-6">Synchronisation Car Maintenance · Détection des doublons</p>

      <Tabs defaultValue="car-maintenance">
        <TabsList>
          <TabsTrigger value="car-maintenance" data-testid="tab-cm"><Cloud className="w-4 h-4 mr-2" /> Car Maintenance</TabsTrigger>
          <TabsTrigger value="doublons" data-testid="tab-doublons-clients"><Users2 className="w-4 h-4 mr-2" /> Doublons clients</TabsTrigger>
        </TabsList>
        <TabsContent value="car-maintenance" className="mt-4"><CarMaintenanceTab /></TabsContent>
        <TabsContent value="doublons" className="mt-4"><DoublonsClientsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// Onglet Car Maintenance
// ============================================================
function CarMaintenanceTab() {
  const [pingState, setPingState] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);

  const ping = async () => {
    setPingState({ loading: true });
    try {
      const { data } = await api.get("/integrations/car-maintenance/ping");
      setPingState({ ok: data.ok, info: data.remote });
    } catch (e) {
      setPingState({ ok: false, error: e.response?.data?.detail || e.message });
    }
  };

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/car-maintenance/last-syncs");
      setHistory(data || []);
    } catch (e) {
      console.error("loadHistory failed:", e);
    }
  }, []);

  useEffect(() => { ping(); loadHistory(); }, [loadHistory]);

  const launchSync = async (dryRun, full) => {
    setSyncing(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/integrations/car-maintenance/sync", { dry_run: dryRun, full });
      setLastResult(data);
      toast.success(dryRun ? "Simulation terminée" : "Synchronisation terminée");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur sync");
    } finally { setSyncing(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 border-gray-200">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              {pingState?.ok && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
              {pingState?.ok === false && <AlertTriangle className="w-5 h-5 text-red-500" />}
              {pingState?.loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Car Maintenance API
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {pingState?.ok && pingState.info?.key_name
                ? `Connecté · clé ${pingState.info.key_name}`
                : pingState?.ok === false
                  ? `Erreur : ${pingState.error || "API inaccessible"}`
                  : "Test de connexion..."}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={ping} data-testid="btn-ping">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Tester
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <button
            onClick={() => launchSync(true, true)}
            disabled={syncing}
            className="text-left p-3 border-2 border-blue-200 rounded-lg hover:border-blue-400 hover:bg-blue-50/40 disabled:opacity-40 transition"
            data-testid="btn-sync-dryrun"
          >
            <div className="text-[10px] uppercase tracking-wider text-blue-700 font-bold mb-1">Simulation</div>
            <div className="font-semibold text-sm text-gray-900">Tester sans rien modifier</div>
            <div className="text-[11px] text-gray-500 mt-0.5">Récupère la liste + simule le matching, sans écrire en base</div>
          </button>
          <button
            onClick={() => launchSync(false, false)}
            disabled={syncing}
            className="text-left p-3 border-2 border-emerald-200 rounded-lg hover:border-emerald-400 hover:bg-emerald-50/40 disabled:opacity-40 transition"
            data-testid="btn-sync-incremental"
          >
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1">Sync incrémentale</div>
            <div className="font-semibold text-sm text-gray-900">Mettre à jour le CRM</div>
            <div className="text-[11px] text-gray-500 mt-0.5">Récupère seulement les nouveautés depuis la dernière sync</div>
          </button>
        </div>

        <button
          onClick={() => {
            if (!window.confirm("Resynchroniser TOUTE la base Car Maintenance ? Peut être long.")) return;
            launchSync(false, true);
          }}
          disabled={syncing}
          className="text-xs text-gray-500 hover:text-gray-900 hover:underline"
          data-testid="btn-sync-full"
        >
          Forcer une synchro complète (full)
        </button>

        {syncing && (
          <div className="mt-3 text-sm text-gray-600 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Synchronisation en cours...
          </div>
        )}

        {lastResult && (
          <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm" data-testid="sync-result">
            <div className="font-semibold text-gray-900 mb-2">
              Résultat {lastResult.dry_run ? "(simulation)" : ""} · sync {lastResult.sync_id?.slice(0, 8)}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <KPI label="Récupérés" value={lastResult.counters?.fetched} />
              <KPI label="Déjà connus" value={lastResult.counters?.matched} color="emerald" />
              <KPI label="Nouveaux" value={lastResult.counters?.created} color="blue" />
              <KPI label="Véhicules créés" value={lastResult.counters?.vehicules_created} />
              {lastResult.counters?.vehicules_other_owner > 0 && (
                <KPI label="Plaque chez autre" value={lastResult.counters.vehicules_other_owner} color="amber" />
              )}
              {lastResult.counters?.errors > 0 && (
                <KPI label="Erreurs" value={lastResult.counters.errors} color="red" />
              )}
            </div>
            {lastResult.warnings_count > 0 && (
              <div className="text-[11px] text-amber-700 mt-2">
                {lastResult.warnings_count} avertissement(s). Voir l'historique pour le détail.
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Historique */}
      <Card className="border-gray-200 overflow-hidden">
        <div className="p-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider font-semibold text-gray-600">
          Historique des synchronisations
        </div>
        {history.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-400">Aucun historique</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase">Type</th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase">Statut</th>
                <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-500 uppercase">Récup.</th>
                <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-500 uppercase">Matchés</th>
                <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-500 uppercase">Nouv.</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-t border-gray-100">
                  <td className="px-3 py-2 text-xs">{fmtDate(h.started_at)}</td>
                  <td className="px-3 py-2 text-xs">
                    {h.dry_run && <Badge variant="outline" className="bg-blue-50 text-blue-700 text-[9px]">Simu</Badge>}
                    {h.full && <Badge variant="outline" className="bg-gray-100 text-gray-700 text-[9px]">Full</Badge>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {h.status === "done" && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 text-[10px]">Terminé</Badge>}
                    {h.status === "error" && <Badge variant="outline" className="bg-red-50 text-red-700 text-[10px]">Erreur</Badge>}
                    {h.status === "running" && <Badge variant="outline" className="bg-amber-50 text-amber-700 text-[10px]">En cours</Badge>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{h.counters?.fetched || 0}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{h.counters?.matched || 0}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{h.counters?.created || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function KPI({ label, value, color = "gray" }) {
  const colors = {
    gray: "bg-gray-100 text-gray-800",
    emerald: "bg-emerald-100 text-emerald-800",
    blue: "bg-blue-100 text-blue-800",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-800",
  };
  return (
    <div className={`${colors[color]} rounded p-2 text-center`}>
      <div className="text-lg font-bold">{value ?? 0}</div>
      <div className="text-[10px] uppercase">{label}</div>
    </div>
  );
}

// ============================================================
// Onglet Doublons
// ============================================================
function DoublonsClientsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fusionDlg, setFusionDlg] = useState(null);

  const scan = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/doublons-clients");
      setData(data);
    } catch (e) { toast.error("Erreur scan"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { scan(); }, [scan]);

  return (
    <div className="space-y-4" data-testid="doublons-tab-content">
      <Card className="p-4 border-gray-200 flex items-center gap-3">
        <div className="flex-1">
          <div className="font-semibold text-gray-900">Doublons potentiels (prénom + nom)</div>
          <div className="text-xs text-gray-500">
            Détecte les fiches avec même prénom + nom (case-insensitive). L'admin décide manuellement de fusionner ou non (jamais automatique).
          </div>
        </div>
        <Button onClick={scan} disabled={loading} variant="outline" size="sm" data-testid="btn-scan-doublons">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </Card>

      {data && (
        <>
          <div className="text-sm text-gray-600">
            {data.total_groupes === 0
              ? "Aucun doublon détecté."
              : `${data.total_groupes} groupe(s) de doublons trouvés`}
          </div>

          {data.groups.map((g, i) => (
            <Card key={i} className="border-amber-200 overflow-hidden" data-testid={`doublon-client-group-${i}`}>
              <div className="p-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3">
                <div>
                  <span className="font-semibold text-amber-900 capitalize">{g.prenom} {g.nom}</span>
                  <span className="ml-2 text-xs text-amber-700">· {g.count} fiches</span>
                </div>
                <Button size="sm" onClick={() => setFusionDlg(g)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`fusionner-${i}`}>
                  <Merge className="w-3.5 h-3.5 mr-1" /> Fusionner
                </Button>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase">Nom complet</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase">Téléphone</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase">Email</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase">Source</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase">Abonn.</th>
                  </tr>
                </thead>
                <tbody>
                  {g.clients.map((c) => (
                    <tr key={c.id} className="border-t border-gray-100">
                      <td className="px-2 py-1.5">
                        {c.prenom} {c.nom}
                        {c.raison_sociale && <span className="text-gray-500"> · {c.raison_sociale}</span>}
                      </td>
                      <td className="px-2 py-1.5 font-mono">{c.tel_principal || "—"}</td>
                      <td className="px-2 py-1.5">{c.email || "—"}</td>
                      <td className="px-2 py-1.5">
                        <Badge variant="outline" className="bg-gray-50 text-gray-600 text-[9px]">{c.source || "—"}</Badge>
                      </td>
                      <td className="px-2 py-1.5">
                        {c.abonnement && c.abonnement !== "aucun" && (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 text-[9px]">{c.abonnement.toUpperCase()}</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ))}
        </>
      )}

      <FusionDialog group={fusionDlg} onClose={() => setFusionDlg(null)} onDone={() => { setFusionDlg(null); scan(); }} />
    </div>
  );
}

function FusionDialog({ group, onClose, onDone }) {
  const [keepId, setKeepId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (group) setKeepId("");
  }, [group]);

  const submit = async () => {
    if (!keepId) { toast.error("Sélectionnez la fiche principale à conserver"); return; }
    const merge_ids = group.clients.filter((c) => c.id !== keepId).map((c) => c.id);
    if (!window.confirm(`Fusionner ${merge_ids.length} fiche(s) DANS celle sélectionnée ? Cette action est irréversible.`)) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/integrations/clients/fusionner", { keep_id: keepId, merge_ids });
      toast.success(`${data.merged_count} fiche(s) fusionnée(s) · ${data.references_basculees.vehicules} véhicules / ${data.references_basculees.or} OR / ${data.references_basculees.factures} factures rattachés`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  if (!group) return null;
  return (
    <Dialog open={!!group} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Fusionner {group.clients.length} fiches "{group.prenom} {group.nom}"</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <p className="text-xs text-gray-500">Sélectionnez la fiche principale à conserver. Les autres seront supprimées et toutes leurs données (véhicules, OR, RDV, factures, devis) seront rattachées à la fiche conservée. Backup automatique dans `clients_fusion_log`.</p>
          {group.clients.map((c) => (
            <label
              key={c.id}
              className={`block p-3 rounded-lg border-2 cursor-pointer ${keepId === c.id ? "border-emerald-400 bg-emerald-50" : "border-gray-200 hover:border-gray-300"}`}
              data-testid={`fusion-pick-${c.id}`}
            >
              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  name="keep"
                  value={c.id}
                  checked={keepId === c.id}
                  onChange={() => setKeepId(c.id)}
                  className="mt-1"
                />
                <div className="flex-1 text-sm">
                  <div className="font-semibold">{c.prenom} {c.nom} {c.raison_sociale && `· ${c.raison_sociale}`}</div>
                  <div className="text-xs text-gray-600">
                    {c.tel_principal && <>📱 {c.tel_principal}</>}
                    {c.email && <> · ✉ {c.email}</>}
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    source={c.source || "—"} · créé {fmtDate(c.created_at)}
                  </div>
                </div>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting || !keepId} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fusion-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Merge className="w-4 h-4 mr-2" />}
            Fusionner
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
